import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './Home.css';

const RETRO_TYPES = [
  {
    id: 'start-stop-continue',
    label: 'Start · Stop · Continue',
    description: 'What should we start doing, stop doing, and keep doing?',
    icon: '🔄',
    available: true,
  },
  {
    id: 'mad-sad-glad',
    label: 'Mad · Sad · Glad',
    description: 'Share what made you mad, sad, or glad this sprint.',
    icon: '😤',
    available: true,
    requiresAuth: true,
  },
  {
    id: '4ls',
    label: '4 Ls',
    description: 'What did you like, learn, lack, and long for?',
    icon: '💡',
    available: true,
    requiresAuth: true,
  },
];

const PREMIUM_TYPES = [
  {
    id: 'retros-against-humanity',
    label: 'Retros Against Humanity',
    description: 'A hilarious, cards-against-humanity-style retrospective for brave teams.',
    icon: '🃏',
  },
];

const TEMPERATURE_TYPES = [
  {
    id: 'emotional-seismograph',
    label: 'Emotional Seismograph',
    description: 'Chart how your emotions shifted throughout the sprint on a shared timeline.',
    icon: '📈',
    available: false,
  },
  {
    id: 'health-check',
    label: 'Health Check',
    description: 'Rate the health of your team across key dimensions like collaboration, delivery, and morale.',
    icon: '🩺',
    available: true,
    requiresAuth: true,
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState(null);
  const [retroName, setRetroName] = useState('');
  const [creating, setCreating] = useState(false);

  async function handleCreate(e) {
    e.preventDefault();
    if (!retroName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: selectedType.id, name: retroName.trim() }),
      });
      const session = await res.json();
      navigate(`/retro/${session.id}`);
    } finally {
      setCreating(false);
    }
  }

  function handleBack() {
    setSelectedType(null);
    setRetroName('');
  }

  return (
    <div className="home">
      <header className="home-header">
        <div className="home-header-top">
          <h1 className="home-logo">retrospekt</h1>
          <div className="home-auth">
            {user === undefined ? null : user ? (
              <Link to="/dashboard" className="home-dashboard-btn">
                <img src={user.picture} alt="" className="home-avatar" />
                My retros
              </Link>
            ) : (
              <a href="/auth/google" className="home-login-btn">
                <GoogleIcon />
                Sign in with Google
              </a>
            )}
          </div>
        </div>
        <p className="home-tagline">Run better retrospectives, together.</p>
        {!user && (
          <p className="home-anon-note">
            Sign in to save your retros. Anonymous retros are deleted after 24 hours.
          </p>
        )}
      </header>

      <main className="home-main">
        {selectedType ? (
          <div className="create-panel">
            <button className="create-back" onClick={handleBack}>← Back</button>
            <div className="create-type-badge">
              <span>{selectedType.icon}</span>
              <span>{selectedType.label}</span>
            </div>
            <h2 className="create-heading">Name your retrospective</h2>
            <form className="create-form" onSubmit={handleCreate}>
              <input
                className="create-input"
                type="text"
                placeholder="e.g. Sprint 42 Retrospective"
                value={retroName}
                onChange={(e) => setRetroName(e.target.value)}
                autoFocus
                maxLength={120}
              />
              <button className="create-btn" type="submit" disabled={!retroName.trim() || creating}>
                {creating ? 'Creating…' : 'Create & get link →'}
              </button>
            </form>
          </div>
        ) : (
          <>
            <h2 className="home-section-title">Choose a retrospective format</h2>
            <div className="retro-grid">
              {RETRO_TYPES.map((retro) => {
                const locked = retro.requiresAuth && user === null;
                return (
                  <button
                    key={retro.id}
                    className={`retro-card ${!retro.available ? 'retro-card--disabled' : ''} ${locked ? 'retro-card--locked' : ''}`}
                    onClick={() => {
                      if (!retro.available) return;
                      if (locked) { navigate('/login'); return; }
                      setSelectedType(retro);
                    }}
                  >
                    <span className="retro-card-icon">{retro.icon}</span>
                    <span className="retro-card-label">{retro.label}</span>
                    <span className="retro-card-desc">{retro.description}</span>
                    {!retro.available && <span className="retro-card-badge">Coming soon</span>}
                    {locked && <span className="retro-card-badge retro-card-badge--auth">🔒 Sign in to use</span>}
                  </button>
                );
              })}
            </div>

            <h2 className="home-section-title home-section-title--feelings">
              <span>🌡️</span> Measure the Temperature
            </h2>
            <div className="retro-grid">
              {TEMPERATURE_TYPES.map((retro) => {
                if (!retro.available) {
                  return (
                    <div key={retro.id} className="retro-card retro-card--feelings">
                      <span className="retro-card-icon">{retro.icon}</span>
                      <span className="retro-card-label">{retro.label}</span>
                      <span className="retro-card-desc">{retro.description}</span>
                      <span className="retro-card-badge retro-card-badge--feelings">Coming soon</span>
                    </div>
                  );
                }
                const locked = retro.requiresAuth && user === null;
                return (
                  <button
                    key={retro.id}
                    className={`retro-card retro-card--feelings ${locked ? 'retro-card--locked' : ''}`}
                    onClick={() => {
                      if (locked) { navigate('/login'); return; }
                      setSelectedType(retro);
                    }}
                  >
                    <span className="retro-card-icon">{retro.icon}</span>
                    <span className="retro-card-label">{retro.label}</span>
                    <span className="retro-card-desc">{retro.description}</span>
                    {locked && <span className="retro-card-badge retro-card-badge--auth">🔒 Sign in to use</span>}
                  </button>
                );
              })}
            </div>

            <h2 className="home-section-title home-section-title--premium">
              <span className="premium-crown">♛</span> Premium
            </h2>
            <div className="retro-grid">
              {PREMIUM_TYPES.map((retro) => (
                <div key={retro.id} className="retro-card retro-card--premium">
                  <span className="retro-card-icon">{retro.icon}</span>
                  <span className="retro-card-label">{retro.label}</span>
                  <span className="retro-card-desc">{retro.description}</span>
                  <span className="retro-card-badge retro-card-badge--premium">Coming soon</span>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
